// frontend/src/components/Mobile/useQrScanner.js
//
// Shared QR scanning for mobile screens. Two ways in, so it works on old
// Android WebViews too (e.g. Android 10 rugged handhelds whose live <video>
// stays black):
//
//   1. Live camera (jsQR). Phones with several rear cameras (wide / macro /
//      depth…) can have Chrome's default "environment" camera deliver only
//      black frames. So every camera is PROBED (~2 s for a real picture):
//      saved camera → default rear → each other camera by deviceId. The first
//      one that works is remembered (localStorage) so next time it opens
//      straight away; switchCamera() cycles to the next one manually.
//   2. Photo capture — decodeFile(file) reads a picture taken by the phone's
//      native camera app (<input type="file" accept="image/*" capture>), so it
//      does not depend on WebView video at all. Uses BarcodeDetector when the
//      browser has it, otherwise jsQR on a downscaled copy.
//
//   const { videoRef, canvasRef, scanning, cameraReady, stalled, error,
//           cameraCount, setError, start, stop, switchCamera, decodeFile } = useQrScanner(onText);
//
// Render <canvas ref={canvasRef} style={{display:'none'}} /> and a
// <video ref={videoRef} autoPlay playsInline muted /> that is ALWAYS in the DOM.
// Don't hide the video with display:none — some old WebViews then never
// decode frames; use the `mb-cam-hidden` class (off-screen, 1px) instead.

import { useState, useRef, useCallback, useEffect } from 'react';
import jsQR from 'jsqr';

const PROBE_MS = 2000;
const STORAGE_KEY = 'mb_qr_camera_id';

const readSavedCamera = () => { try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; } };
const saveCamera = (id) => { try { if (id) localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ } };
const forgetCamera = () => { try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ } };

const listCameras = async () => {
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter((d) => d.kind === 'videoinput' && d.deviceId);
  } catch { return []; }
};
const isFront = (d) => /front|frontal|user|selfie/i.test(d.label || '');

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// true when the video currently shows a non-black picture
const hasPicture = (video) => {
  if (!video || video.readyState < 2 || !video.videoWidth) return false;
  const c = document.createElement('canvas');
  c.width = 32; c.height = 32;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  try {
    ctx.drawImage(video, 0, 0, 32, 32);
    const d = ctx.getImageData(0, 0, 32, 32).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += d[i] + d[i + 1] + d[i + 2];
    return sum / (32 * 32 * 3) > 5;
  } catch {
    return false;
  }
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('imagem inválida')); };
    img.src = url;
  });

const jsqrAtSize = (img, maxDim) => {
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h);
  return jsQR(data.data, w, h, { inversionAttempts: 'attemptBoth' })?.data || null;
};

const useQrScanner = (onResult) => {
  const [scanning, setScanning] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [cameraCount, setCameraCount] = useState(0);
  const [cameras, setCameras] = useState([]);   // [{ deviceId, label }]
  const [debug, setDebug] = useState([]);       // short on-screen diagnostics
  const [error, setError] = useState('');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const runRef = useRef(0); // bumps on every start/stop so stale async work bails out
  const camerasRef = useRef([]);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const log = useCallback((m) => {
    setDebug((d) => [...d.slice(-39), `${new Date().toISOString().slice(14, 22)} ${m}`]);
  }, []);

  const releaseStream = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    runRef.current += 1;
    releaseStream();
    setScanning(false);
    setCameraReady(false);
  }, [releaseStream]);

  useEffect(() => () => stop(), [stop]);

  const loop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) { rafRef.current = requestAnimationFrame(loop); return; }
      ctx.drawImage(video, 0, 0);

      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
      if (code?.data) {
        const text = code.data.trim();
        stop();
        onResultRef.current?.(text);
        return;
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  }, [stop]);

  const activeDeviceId = () =>
    streamRef.current?.getVideoTracks?.()[0]?.getSettings?.().deviceId || '';

  // Attach a stream to the <video> and wait until it really shows a picture.
  // Resolves true (stream kept) or false (stream released).
  const tryStream = useCallback(async (constraints, run) => {
    const tag = JSON.stringify(constraints.video === true ? 'any' : (constraints.video.deviceId?.exact ? `id:${String(constraints.video.deviceId.exact).slice(0, 6)}…` : constraints.video));

    // Give the camera hardware time to be released by the previous attempt —
    // opening another camera right after stop() often fails with NotReadableError.
    releaseStream();
    await sleep(350);
    if (run !== runRef.current) return false;

    let stream = null;
    for (let attempt = 0; attempt < 2 && !stream; attempt += 1) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        log(`${tag}: ${e.name}`);
        if (e.name === 'NotAllowedError' || e.name === 'SecurityError') throw e; // permission: no point retrying
        if (e.name === 'NotReadableError' || e.name === 'AbortError') { await sleep(700); continue; }
        return false;
      }
    }
    if (!stream) return false;
    if (run !== runRef.current) { stream.getTracks().forEach((t) => t.stop()); return false; }

    streamRef.current = stream;

    const video = videoRef.current;
    if (!video) return false;
    video.style.transform = 'translateZ(0)';
    video.style.webkitTransform = 'translateZ(0)';
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('muted', 'true');
    video.muted = true;
    video.srcObject = stream;
    video.play().catch((e) => log(`play(): ${e.name}`));

    const t0 = Date.now();
    while (Date.now() - t0 < PROBE_MS) {
      if (run !== runRef.current) return false;
      if (hasPicture(video)) { log(`${tag}: OK ${video.videoWidth}x${video.videoHeight}`); return true; }
      if (video.paused) video.play().catch(() => {}); // some WebViews need a nudge
      await sleep(150);
    }
    log(`${tag}: sem imagem (ready=${video.readyState} ${video.videoWidth}x${video.videoHeight} paused=${video.paused})`);
    releaseStream();
    return false;
  }, [log, releaseStream]);

  const activate = useCallback(() => {
    setCameraReady(true);
    rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  const failWith = useCallback((msg) => {
    releaseStream();
    setScanning(false);
    setCameraReady(false);
    setStalled(true);
    setError(msg);
  }, [releaseStream]);

  const permissionMessage = (e) =>
    e.name === 'NotAllowedError' ? 'Permissão de câmara negada. Verifique as definições do browser.'
    : e.name === 'NotFoundError' ? 'Câmara não encontrada neste dispositivo.'
    : 'Erro ao aceder à câmara: ' + e.message;

  // Try each camera in `cams` (array of {deviceId}) in order; returns true on first picture
  const tryCameras = useCallback(async (cams, run) => {
    for (const cam of cams) {
      if (run !== runRef.current) return false;
      if (await tryStream({ video: { deviceId: { exact: cam.deviceId } } }, run)) {
        saveCamera(cam.deviceId);
        return true;
      }
    }
    return false;
  }, [tryStream]);

  const start = useCallback(async () => {
    setError('');
    setStalled(false);
    setCameraReady(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      failWith('Câmara não suportada. A app precisa de HTTPS.');
      return;
    }

    runRef.current += 1;
    const run = runRef.current;
    setScanning(true);
    setDebug([]);

    try {
      // 1. camera that worked last time
      const saved = readSavedCamera();
      if (saved) {
        if (await tryStream({ video: { deviceId: { exact: saved } } }, run)) { activate(); return; }
        if (run !== runRef.current) return;
        forgetCamera(); // stale (camera changed / permission reset)
      }

      // 2. browser's default rear camera (also grants permission → real labels)
      if (await tryStream({ video: { facingMode: { ideal: 'environment' } } }, run)) {
        const id = activeDeviceId();
        if (!saved && id) saveCamera(id);
        const cams = await listCameras();
        camerasRef.current = cams;
        setCameraCount(cams.length);
        activate();
        return;
      }
      if (run !== runRef.current) return;

      // 3. every other camera, by deviceId (rear ones first)
      const cams = await listCameras();
      camerasRef.current = cams;
      setCameraCount(cams.length);
      const ordered = [...cams].sort((a, b) => isFront(a) - isFront(b));
      if (await tryCameras(ordered, run)) { activate(); return; }
      if (run !== runRef.current) return;

      // 4. anything at all
      if (await tryStream({ video: true }, run)) { activate(); return; }
      if (run !== runRef.current) return;

      failWith('A câmara abriu mas não mostra imagem neste dispositivo. Use "Tirar foto" ou escreva o código.');
    } catch (e) {
      if (run !== runRef.current) return;
      failWith(permissionMessage(e));
    }
  }, [activate, failWith, tryCameras, tryStream]);

  // Cameras with real labels (asks for permission once if needed) → for a manual picker
  const loadCameras = useCallback(async () => {
    let list = await listCameras();
    if (list.length && !list[0].label) {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ video: true });
        tmp.getTracks().forEach((t) => t.stop());
        await sleep(300);
        list = await listCameras();
      } catch (e) { log(`permissão: ${e.name}`); }
    }
    camerasRef.current = list;
    setCameraCount(list.length);
    setCameras(list.map((d) => ({ deviceId: d.deviceId, label: d.label })));
    log(`câmaras: ${list.map((d, i) => d.label || `#${i + 1}`).join(' | ') || 'nenhuma'}`);
    return list;
  }, [log]);

  // Start a specific camera chosen by the user (and remember it)
  const startWith = useCallback(async (deviceId) => {
    setError('');
    setStalled(false);
    setCameraReady(false);
    runRef.current += 1;
    const run = runRef.current;
    setScanning(true);
    try {
      if (await tryStream({ video: { deviceId: { exact: deviceId } } }, run)) {
        saveCamera(deviceId);
        activate();
        return;
      }
      if (run !== runRef.current) return;
      failWith('Esta câmara não mostra imagem. Escolha outra.');
    } catch (e) {
      if (run !== runRef.current) return;
      failWith(permissionMessage(e));
    }
  }, [activate, failWith, tryStream]);

  // Manual "next camera" (the user can see the picture is wrong/zoomed/blurry)
  const switchCamera = useCallback(async () => {
    runRef.current += 1;
    const run = runRef.current;
    const current = activeDeviceId();
    setCameraReady(false);
    setScanning(true);
    releaseStream();

    try {
      const cams = camerasRef.current.length ? camerasRef.current : await listCameras();
      camerasRef.current = cams;
      setCameraCount(cams.length);
      if (!cams.length) { failWith('Sem outras câmaras disponíveis.'); return; }

      const idx = cams.findIndex((c) => c.deviceId === current);
      const rotated = [...cams.slice(idx + 1), ...cams.slice(0, idx + 1)]; // current one last
      if (await tryCameras(rotated, run)) { activate(); return; }
      if (run !== runRef.current) return;
      failWith('Nenhuma câmara mostra imagem. Use "Tirar foto" ou escreva o código.');
    } catch (e) {
      if (run !== runRef.current) return;
      failWith(permissionMessage(e));
    }
  }, [activate, failWith, releaseStream, tryCameras]);

  // Decode a picture taken with the phone's own camera app
  const decodeFile = useCallback(async (file) => {
    if (!file) return;
    setError('');
    stop();
    try {
      const img = await loadImage(file);

      let text = null;
      if ('BarcodeDetector' in window) {
        try {
          const det = new window.BarcodeDetector({ formats: ['qr_code'] });
          const found = await det.detect(img);
          text = found?.[0]?.rawValue || null;
        } catch { /* fall through to jsQR */ }
      }
      for (const size of [1600, 1000, 2400, 700]) {
        if (text) break;
        text = jsqrAtSize(img, size);
        await sleep(0); // let the UI breathe between attempts
      }

      if (text) onResultRef.current?.(text.trim());
      else setError('Não foi possível ler o QR code da foto. Aproxime-se, evite reflexos e tente de novo.');
    } catch {
      setError('Não foi possível abrir a foto.');
    }
  }, [stop]);

  return { videoRef, canvasRef, scanning, cameraReady, stalled, cameraCount, cameras, debug, error, setError, start, stop, switchCamera, loadCameras, startWith, decodeFile };
};

export default useQrScanner;